<template>
  <section>
    <TopBanner
      title="Loan Payment"
      :src="require('@/assets/images/banners/banner_aboutus.jpg')"
    />

    <v-container>
      <v-stepper v-model="step" vertical>
        <v-stepper-step :complete="step > 1" step="1">
          Verify your loan information.
          <small>Enter your loan number & borrower 4-digit SSN</small>
        </v-stepper-step>

        <v-stepper-content step="1">
          <v-form ref="formMatchLoanInformation" @submit.prevent="nextStep">
            <v-row>
              <v-col cols="12">
                <h3>Find your loan</h3>
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  label="Loan #"
                  v-model="loanNumber"
                  :rules="[required]"
                  outlined
                  dense
                  maxlength="9"
                  hide-details="auto"
                />
              </v-col>

              <v-col cols="12" md="6">
                <v-text-field
                  label="Borrower Last 4-digit SSN"
                  v-model="borrowerSSN4"
                  :rules="[required, last4SSN]"
                  :type="showSSN ? 'text' : 'password'"
                  :append-icon="showSSN ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append="toggleSSN"
                  @input="onSSNInput"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="4"
                  outlined
                  dense
                  hide-details="auto"
                />
              </v-col>
            </v-row>
            <br />
            <v-btn color="primary" elevation="0" type="submit">Next</v-btn>
          </v-form>
        </v-stepper-content>

        <v-stepper-step :complete="step > 2" step="2">
          Select loan payment
        </v-stepper-step>

        <v-stepper-content step="2">
          <v-form ref="formSelectLoanPayment" @submit.prevent="nextStep">
            <v-row v-if="step > 0">
              <v-col cols="12">
                <div v-if="!!loanPayments && loanPayments.length > 0">
                  <v-radio-group
                    v-model="selectedLoanPaymentId"
                    :rules="[required]"
                    required
                  >
                    <v-list two-line>
                      <div
                        v-for="(loanPayment, index) in loanPayments"
                        :key="loanPayment.title + '_' + index"
                      >
                        <v-list-item>
                          <v-list-item-avatar>
                            <v-radio
                              :value="loanPayment.loanPaymentId"
                            ></v-radio>
                          </v-list-item-avatar>

                          <v-list-item-content>
                            <v-list-item-title
                              >Due Date:
                              {{
                                loanPayment.dueDate | moment("MM/DD/YYYY")
                              }}</v-list-item-title
                            >
                            <v-list-item-subtitle>
                              Amount Due:
                              {{ loanPayment.amountDue | currency("$", 0) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>

                          <v-list-item-action>
                            <v-btn icon>
                              <v-icon color="grey lighten-1"
                                >mdi-information</v-icon
                              >
                            </v-btn>
                          </v-list-item-action>
                        </v-list-item>

                        <v-divider
                          v-if="index < loanPayments.length - 1"
                        ></v-divider>
                      </div>
                    </v-list>
                  </v-radio-group>
                </div>
                <div v-else>
                  <v-layout row wrap align-center justify-center>
                    <p class="error--text mt-2">
                      No loan payment history available.
                    </p>
                  </v-layout>
                </div>
              </v-col>
            </v-row>

            <div class="d-flex">
              <v-btn text @click="step -= 1"> Prev </v-btn>
              <v-btn color="primary" elevation="0" type="submit">Next</v-btn>
            </div>
          </v-form>
        </v-stepper-content>

        <v-stepper-step :complete="step > 3" step="3">
          Enter your payment information
        </v-stepper-step>

        <v-stepper-content step="3">
          <div v-if="!!loanPaymentInformationEntry">
            <v-form
              v-if="!loanPaymentInformationEntry.loanPaymentInformationId"
              ref="formLoanPaymentInformationEntry"
              @submit.prevent="nextStep"
            >
              <br />
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.individualName"
                    label="Account Holder Name"
                    :rules="[required, name]"
                    outlined
                    dense
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.routingNumber"
                    label="Routing Number"
                    :rules="[required, routingNumber]"
                    :type="showRouting ? 'text' : 'password'"
                    outlined
                    dense
                    maxlength="9"
                    :append-icon="showRouting ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showRouting = !showRouting"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.accountNumber"
                    label="Account Number"
                    :rules="[required, accountNumber]"
                    :type="showAccount ? 'text' : 'password'"
                    outlined
                    dense
                    maxlength="17"
                    :append-icon="showAccount ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showAccount = !showAccount"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.routingNumberConfirm"
                    label="Confirm Routing Number"
                    :rules="[required, routingNumber, routingMatch]"
                    :type="showRouting ? 'text' : 'password'"
                    outlined
                    dense
                    maxlength="9"
                    :append-icon="showRouting ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showRouting = !showRouting"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.accountNumberConfirm"
                    label="Confirm Account Number"
                    :rules="[required, accountNumber, accountNumberMatch]"
                    :type="showAccount ? 'text' : 'password'"
                    outlined
                    dense
                    maxlength="17"
                    :append-icon="showAccount ? 'mdi-eye' : 'mdi-eye-off'"
                    @click:append="showAccount = !showAccount"
                  ></v-text-field>
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="loanPaymentInformationEntry.amount"
                    label="Amount ($)"
                    :rules="[required, currency]"
                    outlined
                    dense
                    prefix="$"
                    :messages="
                      loanPaymentInformationEntry.amount | currency('$', 2)
                    "
                  ></v-text-field>
                </v-col>
              </v-row>

              <div class="d-flex">
                <v-btn text @click="step -= 1"> Prev </v-btn>
                <v-btn color="primary" elevation="0" type="submit">Next</v-btn>
              </div>
            </v-form>
            <div v-else>
              <v-card class="my-4" max-width="400" outlined>
                <v-card-text>
                  <div class="text-body-1">
                    <strong>Account Holder Name:</strong>
                    {{ loanPaymentInformationEntry.individualName }}
                  </div>
                  <div class="text-body-1">
                    <strong>Account Number:</strong>
                    {{ loanPaymentInformationEntry.routingNumber }}
                  </div>
                  <div class="text-body-1">
                    <strong>Routing Number:</strong>
                    {{ loanPaymentInformationEntry.accountNumber }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn
                    color="error"
                    text
                    @click="
                      deleteLoanInformationEntry(
                        loanPaymentInformationEntry.fileDataId,
                        loanPaymentInformationEntry.loanPaymentInformationId
                      )
                    "
                    >Delete</v-btn
                  >
                </v-card-actions>
              </v-card>
            </div>
          </div>
        </v-stepper-content>

        <v-stepper-step :complete="step > 4" step="4">Result</v-stepper-step>

        <v-stepper-content step="4">
          <v-card class="pa-5" elevation="2" outlined>
            <v-row align="center" justify="center" class="text-center">
              <v-col cols="12">
                <v-icon color="success" large>mdi-check-circle</v-icon>
              </v-col>
              <v-col cols="12">
                <h3 class="font-weight-medium mb-2">
                  Payment Information Received! 🎉
                </h3>
                <p class="text-subtitle-1">
                  Thanks for providing your payment details — you're all set!
                </p>
              </v-col>
            </v-row>
          </v-card>
          <v-btn color="primary" class="my-2" block @click="startOver"
            >Start Over</v-btn
          >
        </v-stepper-content>
      </v-stepper>
    </v-container>
    <Footer></Footer>
  </section>
</template>

<script>
import TopBanner from "@/components/TopBanner";
import Footer from "@/views/layouts/Footer";
import { rules } from "@/mixins";

export default {
  components: {
    TopBanner,
    Footer,
  },
  data() {
    return {
      loanNumber: "", // 800011373
      borrowerSSN4: "", // 0387
      loanPaymentId: null,
      loanPayments: [],
      step: 1,
      isValid: false,
      isValid2: false,
      showSSN: false,
      showRouting: false,
      showAccount: false,
      selectedLoanPaymentId: null,
      routingMatch: () =>
        this.loanPaymentInformationEntry.routingNumber ===
          this.loanPaymentInformationEntry.routingNumberConfirm ||
        "Routing numbers must match",
      accountNumberMatch: () =>
        this.loanPaymentInformationEntry.accountNumber ===
          this.loanPaymentInformationEntry.accountNumberConfirm ||
        "Routing numbers must match",
      loanPaymentInformationEntry: null,
    };
  },
  methods: {
    toggleSSN() {
      this.showSSN = !this.showSSN;
    },
    onSSNInput(value) {
      const raw = String(value).replace(/\D/g, "").slice(0, 4);
      this.borrowerSSN4 = raw;
    },
    async nextStep() {
      this.$store.commit("setGlobalLoading", true);
      try {
        let result = false;

        if (this.step === 1) {
          result = await this.submitCheckingFileDataInformation();
        } else if (this.step === 2) {
          result = await this.findPaymentInformation();
        } else if (this.step === 3) {
          result = await this.submitLoanPaymentInformationEntry();
        }
        this.step += result ? 1 : 0;
      } catch (err) {
        this.$store.commit("SHOW_STATUS_DIALOG", {
          status: "error",
          message: err || "The request timed out.",
        });
      }

      this.$store.commit("setGlobalLoading", false);
    },
    async submitCheckingFileDataInformation() {
      this.loanPayments = [];

      if (this.$refs.formMatchLoanInformation.validate()) {
        const resp = await this.$store
          .dispatch("loanPaymentApi/getLoanPaymentsByLoanNumberAndSSN4", {
            loanNumber: this.loanNumber,
            borrowerSSN4: this.borrowerSSN4,
          })
          .catch((err) => err.response);

        if (resp.status > 300) {
          throw resp.data || "An error occurred.";
        } else {
          this.loanPayments = resp.data.filter(
            (x) => x.loanPaymentType == 0 && !x.receivedDate
          );
        }

        return true;
      }

      return false;
    },
    async findPaymentInformation() {
      if (this.$refs.formSelectLoanPayment.validate()) {
        const loanPayment = this.loanPayments.find(
          (x) => x.loanPaymentId == this.selectedLoanPaymentId
        );
        const resp = await this.$store
          .dispatch(
            "loanPaymentInformationApi/getLoanPaymentInformationEntry",
            {
              fileDataId: loanPayment.fileDataId,
              loanPaymentId: loanPayment.loanPaymentId,
            }
          )
          .catch((err) => err.response);
        console.log(resp);

        if (resp.status > 300) {
          throw resp.data;
        } else {
          this.loanPaymentInformationEntry = resp.data;
        }

        return true;
      }

      return false;
    },
    async deleteLoanInformationEntry(fileDataId, loanPaymentInformationId) {
      if (confirm("Are you sure?")) {
        this.$store.commit("setGlobalLoading", true);
        try {
          const resp = await this.$store
            .dispatch(
              "loanPaymentInformationApi/deleteLoanPaymentInformation",
              {
                fileDataId: fileDataId,
                loanPaymentInformationId: loanPaymentInformationId,
              }
            )
            .catch((err) => err.response);

          if (resp.status > 300) {
            throw resp.data;
          }

          await this.findPaymentInformation();
        } catch (err) {
          this.$store.commit("SHOW_STATUS_DIALOG", {
            status: "error",
            message: err || "The request timed out.",
          });
        }
        this.$store.commit("setGlobalLoading", false);
      }
    },
    async submitLoanPaymentInformationEntry() {
      if (this.$refs.formLoanPaymentInformationEntry.validate()) {
        const loanPayment = this.loanPayments.find(
          (x) => x.loanPaymentId == this.selectedLoanPaymentId
        );

        const resp = await this.$store
          .dispatch("loanPaymentInformationApi/createLoanPaymentInformation", {
            fileDataId: loanPayment.fileDataId,
            loanPaymentId: loanPayment.loanPaymentId,
            entry: this.loanPaymentInformationEntry,
          })
          .catch((err) => err.response);

        if (resp.status > 300) {
          throw resp.data;
        }

        return true;
      }

      return false;
    },
    startOver() {
      window.location.reload();
    },
  },
  mixins: [rules],
};
</script>
